/**
 * The four "linked" panels: the invoices, quotes, contracts and tickets that
 * belong to THIS project.
 *
 * One component with four modes rather than four near-identical files, because
 * the only thing that differs is which call fetches the records and which three
 * strings each row prints. Splitting that four ways would give four places for
 * the same layout bug to be fixed three times.
 *
 * ── THE FILTER IS A NARROWING, NOT A PERMISSION ────────────────────────────
 *
 * Each mode calls the module's OWN gated list route with `{ projectId }`. The
 * server gates `/portal/invoices` on the invoices module whether or not a
 * project id is sent, so a client who may not see invoices sees none here
 * either - and the tab is not even drawn, since `PROJECT_TAB_MODULE` ties each
 * linked tab to the module it borrows from. What the filter buys is that the
 * page does not pull the whole register down and sieve it in the browser.
 *
 * Rows are a summary plus the document itself. Opening the full invoice or
 * contract view is deliberately left to the tab beside this one: this panel
 * answers "what is attached to this project", and duplicating the payment and
 * signing flows inside a project page would be a second place for them to drift.
 */
import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import type { BadgeProps } from "@wa/ui";
import { Badge, Button } from "@wa/ui";
import { useT, useI18n } from "../../../../i18n/I18nProvider";
import { portal, pdfHref } from "../../../../lib/portal";
import { fmtDate, fmtDateTime, fmtAmount, genericStatusVariant, invoiceStatusVariant } from "../../../../lib/format";
import { ListSkeleton } from "../../../ui";

export type LinkedDocsMode =
  | "linked-invoices"
  | "linked-estimates"
  | "linked-contracts"
  | "linked-tickets";

/** What a row prints, once the four record shapes have been flattened. */
interface LinkedRow {
  id: string;
  /** The line the reader scans for: a document number, or a subject when the
   *  record has no number of its own. */
  title: string;
  /** The muted second line, already formatted - dates and money. */
  meta: string[];
  statusLabel: string;
  statusVariant: BadgeProps["variant"];
  /** The document itself. Tickets have none, so their row carries no link. */
  pdfPath: string | null;
}

type Translate = (key: string) => string;

async function fetchRows(
  mode: LinkedDocsMode,
  projectId: string,
  t: Translate,
  locale: string,
): Promise<LinkedRow[]> {
  const filter = { projectId };
  switch (mode) {
    case "linked-invoices": {
      const list = await portal.invoices(filter);
      return list.map((inv) => ({
        id: inv.id,
        title: inv.number,
        meta: [fmtDate(inv.issueDate, locale), fmtAmount(inv.totalCents, inv.currency, locale)],
        statusLabel: t(`portal.invoiceStatus.${inv.status}`),
        statusVariant: invoiceStatusVariant(inv.status),
        pdfPath: `/portal/invoices/${inv.id}/pdf`,
      }));
    }
    case "linked-estimates": {
      const list = await portal.estimates(filter);
      return list.map((est) => {
        // A lapsed quote still arrives with `status: "sent"` - nothing in the
        // CRM ever writes `expired` - so the badge follows the server's
        // `answerState` instead. The same rule, read the same way, as
        // `shownStatus` in EstimatesSection; neither copy derives it from the
        // date, both take the server's answer.
        const shown = est.answerState === "expired" ? "expired" : est.status;
        return {
          id: est.id,
          title: est.number,
          meta: [fmtDate(est.validUntil, locale), fmtAmount(est.totalCents, est.currency, locale)],
          statusLabel: t(`portal.estimateStatus.${shown}`),
          statusVariant: genericStatusVariant(shown),
          pdfPath: `/portal/estimates/${est.id}/pdf`,
        };
      });
    }
    case "linked-contracts": {
      const list = await portal.contracts(filter);
      return list.map((c) => ({
        id: c.id,
        title: c.number ? `${c.number} ${c.title}` : c.title,
        meta: [
          c.startDate || c.endDate ? `${fmtDate(c.startDate, locale)} - ${fmtDate(c.endDate, locale)}` : "",
          c.valueCents != null ? fmtAmount(c.valueCents, c.currency, locale) : "",
        ].filter(Boolean),
        statusLabel: t(`portal.contractStatus.${c.status}`),
        statusVariant: genericStatusVariant(c.status),
        pdfPath: `/portal/contracts/${c.id}/pdf`,
      }));
    }
    case "linked-tickets": {
      const list = await portal.tickets(filter);
      return list.map((tk) => ({
        id: tk.id,
        title: tk.subject,
        meta: [tk.number, fmtDateTime(tk.updatedAt, locale)],
        statusLabel: t(`portal.ticketStatus.${tk.status}`),
        statusVariant: genericStatusVariant(tk.status),
        pdfPath: null,
      }));
    }
  }
}

export function LinkedDocsPanel({ mode, projectId }: { mode: LinkedDocsMode; projectId: string }) {
  const t = useT();
  const { locale } = useI18n();
  const [rows, setRows] = useState<LinkedRow[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setRows(null);
    setFailed(false);
    void fetchRows(mode, projectId, t, locale)
      .then((r) => { if (live) setRows(r); })
      // A failed read says so. Falling back to the empty line would tell the
      // client there is nothing attached to their project, which is a claim this
      // panel has no grounds to make when the request never answered.
      .catch(() => { if (live) setFailed(true); });
    // `live` guards the late answer of a tab the client already left - switching
    // panels twice quickly would otherwise paint the first tab's rows under the
    // third tab's heading.
    return () => { live = false; };
  }, [mode, projectId, locale]); // eslint-disable-line react-hooks/exhaustive-deps

  if (failed) return <p className="py-6 text-center text-sm text-muted-foreground">{t("common.error")}</p>;
  if (!rows) return <div className="-mx-5"><ListSkeleton rows={3} label={t("common.loading")} /></div>;
  if (rows.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{t("portal.projects.linkedEmpty")}</p>;
  }

  return (
    <ul className="divide-y divide-border">
      {rows.map((row) => (
        <li key={row.id} className="flex flex-wrap items-center gap-3 py-3">
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium text-foreground">{row.title}</div>
            {row.meta.length > 0 && (
              <div className="mt-0.5 text-xs text-muted-foreground">{row.meta.join(" · ")}</div>
            )}
          </div>
          <Badge variant={row.statusVariant} className="shrink-0">{row.statusLabel}</Badge>
          {row.pdfPath && (
            <Button variant="ghost" size="sm" asChild className="shrink-0 text-muted-foreground">
              <a href={pdfHref(row.pdfPath)} target="_blank" rel="noreferrer">
                <Download /> PDF
              </a>
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}
